import React, { useState, useRef, useEffect } from 'react';
import './ConversationSearch.css';

const ConversationSearch = ({
    searchQuery,
    setSearchQuery,
    isSearchActive,
    toggleSearch,
    clearSearch,
    searchStats,
    searchFilters,
    updateFilter,
    searchHistory,
    navigateToNext,
    navigateToPrevious,
    searchInputRef,
    currentMatchMessage,
    onNavigateToMessage
}) => {
    const [showFilters, setShowFilters] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const filterMenuRef = useRef(null);
    const historyMenuRef = useRef(null);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
                setShowFilters(false);
            }
            if (historyMenuRef.current && !historyMenuRef.current.contains(event.target)) {
                setShowHistory(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
    };

    const handleHistorySelect = (term) => {
        setSearchQuery(term);
        setShowHistory(false);
        searchInputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (e.ctrlKey || e.metaKey) {
                navigateToPrevious();
            } else {
                navigateToNext();
            }
        } else if (e.key === 'Escape') {
            toggleSearch();
        }
    };

    const navigateToCurrentMatch = () => {
        if (currentMatchMessage && onNavigateToMessage) {
            onNavigateToMessage(currentMatchMessage);
        }
    };

    if (!isSearchActive) {
        return (
            <button 
                className="search-toggle-btn"
                onClick={toggleSearch}
                title="Rechercher dans la conversation (Ctrl+F)"
            >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        );
    }

    return (
        <div className="conversation-search">
            <div className="search-bar">
                <div className="search-input-container">
                    <svg className="search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Rechercher dans les messages..."
                        className="search-input"
                        autoFocus
                    />
                    
                    {searchQuery && (
                        <button
                            className="search-clear-btn"
                            onClick={clearSearch}
                            title="Effacer la recherche"
                        >
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    )}
                    
                    <button
                        className="search-history-btn"
                        onClick={() => setShowHistory(!showHistory)}
                        title="Historique de recherche"
                        disabled={searchHistory.length === 0}
                    >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16z" stroke="currentColor" strokeWidth="2"/>
                            <path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                    
                    <button
                        className="search-filters-btn"
                        onClick={() => setShowFilters(!showFilters)}
                        title="Filtres de recherche"
                    >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 6h12M7 10h6M9 14h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                    
                    <button
                        className="search-close-btn"
                        onClick={toggleSearch}
                        title="Fermer la recherche"
                    >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                {/* Search History Dropdown */}
                {showHistory && searchHistory.length > 0 && (
                    <div ref={historyMenuRef} className="search-history-menu">
                        <div className="search-history-header">Recherches récentes</div>
                        {searchHistory.map((term, index) => (
                            <button
                                key={index}
                                className="search-history-item"
                                onClick={() => handleHistorySelect(term)}
                            >
                                {term}
                            </button>
                        ))}
                    </div>
                )}

                {/* Search Filters */}
                {showFilters && (
                    <div ref={filterMenuRef} className="search-filters-menu">
                        <div className="search-filters-header">Filtres de recherche</div>
                        
                        <div className="filter-group">
                            <label>Type de message:</label>
                            <select
                                value={searchFilters.messageType}
                                onChange={(e) => updateFilter('messageType', e.target.value)}
                            >
                                <option value="all">Tous les messages</option>
                                <option value="text">Texte uniquement</option>
                                <option value="media">Images et vidéos</option>
                                <option value="audio">Messages audio</option>
                                <option value="files">Documents et fichiers</option>
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Expéditeur:</label>
                            <select
                                value={searchFilters.sender}
                                onChange={(e) => updateFilter('sender', e.target.value)}
                            >
                                <option value="all">N'importe qui</option>
                                <option value="me">Mes messages</option>
                                <option value="other">Autre personne</option>
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Période:</label>
                            <select
                                value={searchFilters.dateRange}
                                onChange={(e) => updateFilter('dateRange', e.target.value)}
                            >
                                <option value="all">N'importe quand</option>
                                <option value="today">Aujourd'hui</option>
                                <option value="week">Cette semaine</option>
                                <option value="month">Ce mois-ci</option>
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Pièces jointes:</label>
                            <select
                                value={searchFilters.hasAttachments}
                                onChange={(e) => updateFilter('hasAttachments', e.target.value)}
                            >
                                <option value="all">Tous</option>
                                <option value="yes">Avec pièces jointes</option>
                                <option value="no">Texte uniquement</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Search Results Navigation */}
            {searchQuery && (
                <div className="search-navigation">
                    <div className="search-stats">
                        {searchStats.searchMatches > 0 ? (
                            <>
                                {searchStats.currentMatch} sur {searchStats.searchMatches} résultat{searchStats.searchMatches > 1 ? 's' : ''}
                                {searchStats.isFiltered && (
                                    <span className="filtered-indicator">
                                        ({searchStats.totalResults} résultat{searchStats.totalResults > 1 ? 's' : ''} total{searchStats.totalResults > 1 ? 'aux' : ''})
                                    </span>
                                )}
                            </>
                        ) : (
                            searchStats.isFiltered ? 
                                `${searchStats.totalResults} résultat${searchStats.totalResults > 1 ? 's' : ''} (aucune correspondance textuelle)` :
                                'Aucun résultat trouvé'
                        )}
                    </div>
                    
                    {searchStats.searchMatches > 0 && (
                        <div className="search-navigation-buttons">
                            <button
                                className="nav-btn"
                                onClick={navigateToPrevious}
                                disabled={searchStats.searchMatches <= 1}
                                title="Résultat précédent (Ctrl+Shift+Enter)"
                            >
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 13l-5-5-5 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <button
                                className="nav-btn"
                                onClick={navigateToCurrentMatch}
                                title="Aller au résultat actuel"
                            >
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 10l3 3 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <button
                                className="nav-btn"
                                onClick={navigateToNext}
                                disabled={searchStats.searchMatches <= 1}
                                title="Résultat suivant (Enter)"
                            >
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Active Filters Indicator */}
            {searchStats.isFiltered && Object.values(searchFilters).some(f => f !== 'all') && (
                <div className="active-filters">
                    <span className="active-filters-label">Filtres actifs:</span>
                    {Object.entries(searchFilters).map(([key, value]) => {
                        if (value === 'all') return null;
                        const labelMap = {
                            messageType: `Type: ${value}`,
                            sender: `Sender: ${value}`,
                            dateRange: `Date: ${value}`,
                            hasAttachments: `Attachments: ${value}`
                        };
                        return (
                            <span key={key} className="filter-chip">
                                {labelMap[key]}
                                <button
                                    className="filter-chip-remove"
                                    onClick={() => updateFilter(key, 'all')}
                                >
                                    ✕
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ConversationSearch;
